import React, { useState } from 'react';
import { Search, Filter, Shield, User, Circle, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getStaffDisplayName } from '../../../utils/staffDisplay';

const ProviderList = ({ providers, onSelectProvider, onRenew }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');

    const filteredProviders = providers.filter(provider => {
        // Search by name, codename, or discord ID
        const searchLower = searchTerm.toLowerCase();
        const nameMatch = provider.name?.toLowerCase().includes(searchLower);
        const codenameMatch = provider.staffDetails?.preStaffData?.codename?.toLowerCase().includes(searchLower)
            || provider.staffDetails?.contractData?.codename?.toLowerCase().includes(searchLower);
        const idMatch = provider.discordId?.includes(searchLower);
        const matchesSearch = nameMatch || codenameMatch || idMatch;

        // Filter by role
        const matchesRole = roleFilter === 'all' || provider.accessLevel === roleFilter;

        return matchesSearch && matchesRole;
    });

    const getRoleStyle = (role) => {
        const styles = {
            'admin': 'bg-red-500/10 text-red-500 border-red-500/20',
            'head_staff': 'bg-orange-500/10 text-orange-500 border-orange-500/20',
            'head_suporte': 'bg-orange-500/10 text-orange-500 border-orange-500/20',
            'head_moderacao': 'bg-orange-500/10 text-orange-500 border-orange-500/20',
            'analista_qualidade': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
            'programador': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
            'moderador': 'bg-purple-500/10 text-purple-500 border-purple-500/20',
            'suporte': 'bg-green-500/10 text-green-500 border-green-500/20',
            'suporte_compras': 'bg-green-500/10 text-green-500 border-green-500/20',
            'suporte_descontos': 'bg-green-500/10 text-green-500 border-green-500/20',
            'criador_de_conteudo': 'bg-pink-500/10 text-pink-500 border-pink-500/20',
            'user': 'bg-gray-500/10 text-gray-500 border-gray-500/20'
        };
        return styles[role] || 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    };

    const getStatusInfo = (provider) => {
        const contract = provider.staffDetails?.contractData;
        const personal = provider.staffDetails?.personalData;
        const business = provider.staffDetails?.businessData;
        const dataRequest = provider.staffDetails?.dataUpdateRequest;
        const daysRemaining = provider.stats?.daysRemaining;

        // 1. SUSPENSO
        if (provider.staffDetails?.isSuspended) {
            return { label: 'SUSPENSO', color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/20', dot: 'bg-red-500' };
        }

        // 2. ENCERRADO (Ciclo Fechado)
        if (contract?.terminationSignedAt || contract?.status === 'terminated' || contract?.status === 'liquidated') {
            return { label: 'ENCERRADO', color: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/20', dot: 'bg-gray-500' };
        }

        // 3. AGUARDANDO QUITAÇÃO (Desligamento Iniciado)
        if (contract?.terminationStartedAt || contract?.status === 'liquidating') {
            return { label: 'AGUARDANDO QUITAÇÃO', color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/20', dot: 'bg-orange-500' };
        }

        // 4. CONTRATO VENCIDO
        if (contract?.onboardingCompletedAt && daysRemaining !== undefined && daysRemaining <= 0) {
            return { label: 'CONTRATO VENCIDO', color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/20', dot: 'bg-red-400' };
        }

        // 5. ASSINATURA DOCUSIGN
        if (contract?.status === 'pending_docusign') {
            return { label: 'ASSINATURA DOCUSIGN', color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20', dot: 'bg-blue-500' };
        }

        // 6. DADOS SOLICITADOS
        if (dataRequest?.status === 'pending') {
            return { label: 'DADOS SOLICITADOS', color: 'text-yellow-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', dot: 'bg-yellow-500' };
        }

        // 7. RENOVAÇÃO PRÓXIMA
        if (contract?.onboardingCompletedAt && daysRemaining !== undefined && daysRemaining <= 30) {
            return { label: 'RENOVAÇÃO PRÓXIMA', color: 'text-yellow-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', dot: 'bg-yellow-500' };
        }

        // 8. PERFIL INCOMPLETO
        const hasCodename = provider.staffDetails?.contractData?.codename || provider.staffDetails?.preStaffData?.codename;
        if (contract?.onboardingCompletedAt && (!provider.avatar || !hasCodename)) {
            return { label: 'PERFIL INCOMPLETO', color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20', dot: 'bg-purple-500' };
        }

        // 9. CONTRATO EM PREENCHIMENTO
        if (contract?.onboardingStartedAt && !contract?.onboardingCompletedAt) {
            return { label: 'EM PREENCHIMENTO', color: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/20', dot: 'bg-blue-400' };
        }

        // 10. PRÉ-CADASTRO REALIZADO
        if (!contract?.onboardingStartedAt) {
            return { label: 'PRÉ-CADASTRO', color: 'text-gray-400', bg: 'bg-gray-400/10', border: 'border-gray-400/20', dot: 'bg-gray-400' };
        }

        // 11. ATIVO (Fallback)
        return { label: 'ATIVO', color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', dot: 'bg-emerald-500' };
    };

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                        placeholder="Buscar por nome, codinome ou ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 bg-gray-900/50 border-gray-800 text-white focus:border-purple-500"
                    />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-full md:w-[200px] bg-gray-900/50 border-gray-800 text-white">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Filtrar por Cargo" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-800 text-white">
                        <SelectItem value="all">Todos os Cargos</SelectItem>
                        <SelectItem value="suporte">Suporte</SelectItem>
                        <SelectItem value="moderador">Moderador</SelectItem>
                        <SelectItem value="programador">Programador</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="head_staff">Heads</SelectItem>
                        <SelectItem value="analista_qualidade">Qualidade</SelectItem>
                        <SelectItem value="criador_de_conteudo">Criador de Conteúdo</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* List */}
            <div className="grid gap-3">
                {filteredProviders.length > 0 ? (
                    filteredProviders.map((provider) => {
                        const status = getStatusInfo(provider);
                        const daysRemaining = provider.stats?.daysRemaining || 0;
                        const isExpiring = daysRemaining <= 30;

                        return (
                            <div
                                key={provider.discordId}
                                onClick={() => onSelectProvider(provider)}
                                className="group relative bg-gray-900/40 hover:bg-gray-900/60 border border-white/5 hover:border-purple-500/30 rounded-xl p-4 transition-all cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4"
                            >
                                <div className="flex items-center gap-4">
                                    {/* Avatar */}
                                    <div className="relative shrink-0">
                                        <div className="w-12 h-12 rounded-full bg-gray-800 overflow-hidden ring-2 ring-white/10 group-hover:ring-purple-500/50 transition-all">
                                            {provider.avatar ? (
                                                <img
                                                    src={`https://cdn.discordapp.com/avatars/${provider.discordId}/${provider.avatar}.png`}
                                                    alt={provider.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <User className="w-full h-full p-2 text-gray-400" />
                                            )}
                                        </div>
                                        <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-gray-900 ${status.dot}`} />
                                    </div>

                                    {/* Info */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-white font-bold text-lg">
                                                {getStaffDisplayName(provider)}
                                            </h3>
                                            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider border ${getRoleStyle(provider.accessLevel)}`}>
                                                {provider.accessLevel?.replace('_', ' ')}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-gray-400 hidden group-hover:block transition-all duration-300">
                                            {provider.staffDetails?.personalData?.fullName || 'Nome não informado'}
                                        </p>
                                    </div>
                                </div>

                                {/* Right Side Stats & Status */}
                                <div className="flex items-center justify-between md:justify-end gap-8 w-full md:w-auto mt-2 md:mt-0">

                                    {/* Contract Time */}
                                    <div className="text-right">
                                        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-0.5">Contrato</p>
                                        <div className={`text-sm font-mono font-bold ${isExpiring ? 'text-red-400' : 'text-gray-300'}`}>
                                            {daysRemaining > 0 ? `${daysRemaining} dias` : 'Expirado'}
                                        </div>
                                    </div>

                                    {/* Contract Value */}
                                    <div className="text-right hidden sm:block">
                                        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-0.5">Valor</p>
                                        <p className="text-sm text-emerald-400 font-mono font-bold">
                                            R$ {provider.staffDetails?.contractData?.contractValue || '0'}
                                        </p>
                                    </div>

                                    {/* Status Badge */}
                                    <div className={`px-3 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider ${status.bg} ${status.color} ${status.border} shadow-lg shadow-black/20`}>
                                        {status.label}
                                    </div>

                                    {/* Renewal Action */}
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-9 w-9 hover:bg-blue-500/20 hover:text-blue-400 rounded-lg transition-all"
                                        title="Renovar Contrato"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRenew && onRenew(provider);
                                        }}
                                    >
                                        <Calendar className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center py-12 text-gray-500">
                        Nenhum prestador encontrado.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProviderList;
